// Solo (the game modes spec, Play alone on screen): the player against solo players the game runs.
// Online it is the server's round: a solo park the api opens for this player alone, decided and paid
// by the server like any round. With no network it falls back to the phone, with no server at all:
// the same local room as the tutorial (the worker's own Room, its simulation and its bots) without
// the tutorial's script, paying nothing. Either way: pick a game, pick a side, Start. No code, no
// lobby wait, no Ready. The word bot never shows: the others are solo players with Lagos names.
import { PRESETS, type PresetName } from '@wahala/config';
import { enterPark, errorText } from '../App';
import { loadRace, raceKit } from '../game/raceKit';
import { ParkSession } from '../game/session';
import { api, type ParkPreset } from '../lib/api';
import { useUi } from '../store/ui';
import { S } from '../strings';
import type { ParkLoadInfo } from '../ui/parkLoad';
import type { LocalPark } from './local';
import { playAlone, soloStep } from './playAlone';

export type SoloSide = 'driver' | 'agbero' | 'surprise';

/** How many seats a Solo round fills to (Race fills to six as well). */
const SOLO_SEATS = 6;
const LEVEL_KEY = 'wahala.solo.level';
const MAX_LEVEL = 5;

/**
 * The solo players' level, 1 to 5: it follows the player's own record, a step up after a Solo round
 * they won and a step down after one they lost, so rounds stay close. Kept on this phone.
 */
export function soloLevel(): number {
  try {
    const n = Number(localStorage.getItem(LEVEL_KEY));
    return n >= 1 && n <= MAX_LEVEL ? Math.round(n) : 1;
  } catch {
    return 1;
  }
}
function saveLevel(n: number): void {
  try {
    localStorage.setItem(LEVEL_KEY, String(n));
  } catch {
    // remembered only when the browser lets us
  }
}

/**
 * Everything a Solo round of `type` needs from the network, fetched once: the room and its bots
 * (most of a worker, so not in the menu's shell), and for a race its rules, the ring, the race
 * driver, the race screens and the road's race layers. The Solo sheet calls it as soon as a game is
 * picked, so a round started after that plays with no network at all.
 */
const kits = new Map<string, Promise<typeof import('./local')>>();
export function preloadSolo(type: string): Promise<typeof import('./local')> {
  const race = PRESETS[type as PresetName]?.mode === 'race';
  const key = race ? 'race' : 'room';
  let kit = kits.get(key);
  if (!kit) {
    kit = Promise.all([
      import('./local'),
      race ? loadRace() : null,
      race ? import('@wahala/game-worker/bots').then((b) => b.loadRaceDriver()) : null,
      race ? import('../screens/Race') : null,
      race ? import('../game/race-layer') : null,
      race ? import('../game/world3d/race3d') : null,
      race ? import('../game/ring-art') : null,
      race ? import('../game/world3d/ring') : null,
    ]).then(([l]) => l);
    // a failed fetch (the network went) is tried again next time
    kit.catch(() => kits.delete(key));
    kits.set(key, kit);
  }
  return kit;
}

/**
 * `type` is a game type the server lists (the room plays the Park for one it doesn't know). Online:
 * the server's solo park; no network: the round on the phone, which pays nothing.
 */
export function startSolo(type: string, side: SoloSide, look?: ParkLoadInfo['look']): void {
  const race = PRESETS[type as PresetName]?.mode === 'race';
  const pick: SoloSide = race ? 'surprise' : side;
  // the way-in screen covers the menu while the seat (or the round's kit) arrives
  const name = useUi.getState().me?.name;
  const known: ParkLoadInfo = { type, solo: true, ...(name ? { driver: name } : {}), ...(look ? { look } : {}) };
  useUi.getState().set({ joining: true, entering: known, notice: null });
  const onPhone = forcedOnPhone();
  void playAlone({
    online: () => !onPhone && (typeof navigator === 'undefined' || navigator.onLine !== false),
    server: () => api.soloPark(type as ParkPreset, pick, soloLevel()),
    enter: (t) => {
      enterPark(t, known);
      followLevel();
      // test mode only: the e2e specs read the session (a server round has no local room)
      const session = useUi.getState().park;
      if (useUi.getState().config?.mode === 'test' && session)
        (window as unknown as { __solo?: unknown }).__solo = { session, type, side: pick, room: () => null };
    },
    local: () => startLocal(type, pick, !onPhone),
    refuse: (e) => useUi.getState().set({ joining: false, notice: errorText(e) }),
  });
}

/**
 * A server round over: the level this phone keeps moves the step the server moved its solo drivers,
 * so the next Play alone starts where this one left off. Once per round.
 */
function followLevel(): void {
  const session = useUi.getState().park;
  if (!session) return;
  let counted = '';
  const off = session.world.subscribe(() => {
    if (useUi.getState().park !== session) return off();
    const v = session.world.view;
    const round = session.world.lobby?.matchId ?? '';
    const step = v ? soloStep(v) : null;
    if (step === null || !round || round === counted) return;
    counted = round;
    saveLevel(Math.max(1, Math.min(MAX_LEVEL, soloLevel() + step)));
  });
}

/**
 * Test mode only: a spec that reaches into the local room asks for the round on the phone
 * (localStorage wahala.solo.onPhone = 1). Never read in a live build's mode.
 */
function forcedOnPhone(): boolean {
  if (useUi.getState().config?.mode !== 'test') return false;
  try {
    return localStorage.getItem('wahala.solo.onPhone') === '1';
  } catch {
    return false;
  }
}

/** The round on the phone (no network): no server, nothing paid, and a line that says so. */
function startLocal(type: string, side: SoloSide, tell: boolean): void {
  preloadSolo(type).then(
    (l) => {
      runSolo(l.LocalPark, type, side);
      if (tell) useUi.getState().park?.world.toast(S.play.soloOffline, 'info', 6000);
    },
    () => useUi.getState().set({ joining: false, notice: S.play.soloLoad, noticeTone: 'wahala' }),
  );
}

function runSolo(Local: typeof LocalPark, type: string, side: SoloSide): void {
  const ui = useUi.getState();
  ui.park?.leave();
  let local: LocalPark | null = null;
  let level = soloLevel();
  /** The round whose result already moved the level. */
  let counted = '';
  const session = new ParkSession(
    { code: 'SOLO', roomId: 'SOLO', workerUrl: '', ticket: '' },
    () => {
      const u = useUi.getState();
      if (u.park === session) u.set({ park: null, practice: null, screen: 'menu' });
    },
    {
      net: (o) => {
        local = new Local(o, {
          name: ui.me?.name ?? 'You',
          preset: type,
          solo: {
            side: side === 'agbero' ? 'impostor' : side === 'driver' ? 'crew' : null,
            fill: SOLO_SEATS,
            skill: level,
          },
          onTick: (room) => {
            const me = local?.me;
            // no lobby wait: the round starts as soon as the park is there, and again after Play again
            if (me && room.phase === 'WAITING' && room.host === me.id) local?.act({ t: 'lobby.start' } as never);
            // a round over: the solo players move a step toward the player's own level
            const m = room.match;
            if (me && room.phase === 'RESULTS' && m?.winner && room.matchId !== counted) {
              counted = room.matchId;
              // a race: a step up for first over the line, down for the back half, else it stays
              const order = m.race ? (raceKit()?.raceOrder(m) ?? []) : null;
              const at = order ? order.indexOf(me.id) : -1;
              const step = order
                ? at === 0
                  ? 1
                  : at >= Math.ceil(order.length / 2)
                    ? -1
                    : 0
                : m.players[me.id]?.team === m.winner.team
                  ? 1
                  : -1;
              level = Math.max(1, Math.min(MAX_LEVEL, level + step));
              saveLevel(level);
              for (const b of room.members) if (b.bot) b.bot.skill = level;
            }
          },
        });
        return local;
      },
    },
  );
  ui.set({ park: session, practice: null, screen: 'park', notice: null, joining: false });
  // test mode only: the e2e specs read the session and may set a scene up in the local room
  if (ui.config?.mode === 'test')
    (window as unknown as { __solo?: unknown }).__solo = {
      session,
      type,
      side,
      room: () => (local as LocalPark | null)?.room ?? null,
    };
}
